import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { dateFromNow, dateFromNowStrict, logger, numberFormat } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { ChannelsOrganization } from 'entities/Organization/channels/organization'
import { OrganizationApi } from 'entities/Organization/api/organization'
import {
  type IParamsOrganizationQuietHours,
  type IParamsTrackOrganizationActivity,
  type IResponseEventOrganizationCalling,
  type IResponseEventUpdateBalance,
  type IResponseNumberVendor,
  type IResponseOrganization,
  type IResponseOrganizationCall,
} from 'entities/Organization/api/types'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { Attachment } from 'entities/Attachment/model/Attachment'

export class OrganizationStore {
  initPromise
  private _setFinishInit: ((value?: unknown) => void) | null = null
  private _loading = false
  private _accountCredits = 0
  private _trialCredits = 0
  private _rolloverCredits = 0
  private _purchasedCredits = 0
  private _id: number | null = null
  private _owner_id: number | null = null
  private _account_balance: number | null = null
  private _name: string | null = null
  private _created_at: string | null = null
  private _organization_call: IResponseOrganizationCall | null = null
  private _double_opt_in: { is_active: boolean } = { is_active: false }
  private _trial_ends_at = ''
  private _number_vendor: IResponseNumberVendor | null = null
  private _is_agency = false
  private _agency_id: number | null = null
  private _is_contract = false
  private _fallback_team_id: number | null = null
  private _hash_id: string | null = null
  private _original: IResponseOrganization | null = null

  private _trialDaysMaxTotal = 14
  private _isTrialEnded = true
  private _updateTrialEndsAtStringTrigger = ''
  private _updateTrialEndsAtStringIdInterval: NodeJS.Timeout | null = null
  private _broadcast_limits: IResponseOrganization['broadcast_limits'] | null = null
  private _phone_checker: IResponseOrganization['phone_checker'] | null = null
  private _auto_close_setting: IResponseOrganization['auto_close_setting'] | null = null
  private _auto_close_unsubscribe: IResponseOrganization['auto_close_unsubscribe'] | null = null
  private _extended_international_texting: IResponseOrganization['extended_international_texting'] =
    false
  private _use_global_conversation_history: IResponseOrganization['use_global_conversation_history'] =
    false
  private _auto_close_days: IResponseOrganization['auto_close_days'] | null = null
  private _two_fa_enabled: IResponseOrganization['two_fa_enabled'] = null
  private _auto_assign_contact_owner: IResponseOrganization['auto_assign_contact_owner'] = false
  private _create_new_conv_via_aircall = false
  private _is_numbers_grace_period: IResponseOrganization['is_numbers_grace_period'] = false
  private _voicemailGreetingsAttachments: Attachment[] = []
  private _is_hipaa_compliant = false
  private _hipaa_enabled_at: string | null = null
  private _quietHoursSettings: IResponseOrganization['quiet_hours_settings'] = null
  private _rollover_credits_remove_date: string | null = null
  private _franchise: IResponseOrganization['franchise'] = false
  private _clients_plan: IResponseOrganization['clients_plan'] = null
  private _is_ach_enabled = false
  hasAffiliateLink = false

  constructor(private _entities: EntitiesStore) {
    makeAutoObservable(this)
    reaction(() => this._updateTrialEndsAtStringTrigger, this.updateTrialEndsAtString)

    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })
  }

  get is_contract() {
    return this._is_contract
  }

  get isAchEnabled() {
    return this._is_ach_enabled
  }

  get isTrialEnded() {
    return this._isTrialEnded
  }

  get loading() {
    return this._loading
  }

  get broadcast_limits() {
    return this._broadcast_limits
  }

  get trialDaysMaxTotal() {
    return this._trialDaysMaxTotal
  }

  get trialCredits() {
    return this._trialCredits
  }

  get accountCredits() {
    return this._accountCredits
  }

  get rolloverCredits() {
    return this._rolloverCredits
  }

  get rolloverCreditsRemoveDate() {
    return this._rollover_credits_remove_date
  }

  get purchasedCredits() {
    return this._purchasedCredits
  }

  get organization_call() {
    return this._organization_call
  }

  get agency_id() {
    return this._agency_id
  }

  get isClient() {
    return !!this.agency_id
  }

  get account_balance() {
    return this._account_balance
  }

  get name() {
    return this._name
  }

  get created_at() {
    return this._created_at
  }

  get id() {
    return this._id
  }

  get original() {
    return this._original
  }

  get owner() {
    return this._entities.usersStore.getItem(this._owner_id)
  }

  get isTwilioNumberVendor() {
    return this._number_vendor?.key === 'twilio-integration'
  }
  get isTwilioSubAccountNumberVendor() {
    return this._number_vendor?.key === 'twilio'
  }

  get trialEndsAtString() {
    if (this._isTrialEnded) return ''

    return dateFromNow(this.trialEndsAt)
  }

  get strictTrialEndsAtString() {
    if (this._isTrialEnded) return ''

    return dateFromNowStrict(this.trialEndsAt)
  }

  get isSinchNumberVendor() {
    return this._number_vendor?.key === 'sinch'
  }

  updateDoubleOptIn = (isActive: boolean) => {
    runInAction(() => {
      this._double_opt_in = { is_active: isActive }
    })
  }

  updateExtendedInternationalTexting = (isActive: boolean) => {
    this._extended_international_texting = isActive
  }

  updateOrganizationName = (name: string) => {
    this._name = name
  }

  updateTrialEndsAtString = () => {
    const remainingDiff = uiStore.dayjs(this.trialEndsAt).diff(uiStore.dayjs(), 'day', true)
    const trialEndsAtDays = Math.ceil(remainingDiff)
    this._isTrialEnded = trialEndsAtDays <= 0

    if (this._isTrialEnded) {
      this._updateTrialEndsAtStringIdInterval &&
        clearInterval(this._updateTrialEndsAtStringIdInterval)
    }
  }

  fetchOrganization = async () => {
    try {
      this._loading = true

      const { data: organization } = await OrganizationApi.getOrganizationCurrent()

      runInAction(() => {
        this._id = organization.id
        this._owner_id = organization.owner_id
        this._accountCredits = organization.account_credits
        this._trialCredits = organization.trial_credits
        this._rolloverCredits = organization.credits.rollover_credits
        this._purchasedCredits = organization.credits.purchased
        this._account_balance = organization.credits.account_balance
        this._name = organization.name
        this._created_at = organization.created_at
        this._organization_call = organization.organization_call
        this._double_opt_in = organization.double_opt_in
        this._trial_ends_at = organization.trial_ends_at
        this._number_vendor = organization.number_vendor
        this._is_agency = organization.is_agency
        this._agency_id = organization.agency_id
        this._broadcast_limits = organization.broadcast_limits
        this._phone_checker = organization.phone_checker
        this._is_contract = !!organization.is_contract
        this._fallback_team_id = organization.fallback_team?.id || null
        this._hash_id = organization.hash_id
        this._original = organization
        this._auto_close_setting = organization.auto_close_setting
        this._auto_close_days = organization.auto_close_days
        this._two_fa_enabled = organization.two_fa_enabled
        this._auto_close_unsubscribe = organization.auto_close_unsubscribe
        this._extended_international_texting = organization.extended_international_texting
        this._use_global_conversation_history = organization.use_global_conversation_history
        this._auto_assign_contact_owner = organization.auto_assign_contact_owner
        this._create_new_conv_via_aircall =
          organization.organization_call?.['create-new-conv-via-aircall']?.is_active ?? false
        this._is_numbers_grace_period = organization.is_numbers_grace_period
        this._voicemailGreetingsAttachments = organization.voicemail_greetings.map(
          (item) => new Attachment({ responseVoicemailGreetings: item })
        )
        this._is_hipaa_compliant = organization.hipaa.is_hipaa_compliant
        this._hipaa_enabled_at = organization.hipaa.enabled_at
        this._quietHoursSettings = organization.quiet_hours_settings
        this._rollover_credits_remove_date = organization.rollover_credits_remove_date
        this._franchise = organization.franchise
        this._clients_plan = organization.clients_plan
        this._is_ach_enabled = organization.is_ach_enabled ?? false
        this.hasAffiliateLink = organization.hasAffiliateLink ?? false

        if (organization.trial_ends_at) {
          this._updateTrialEndsAtStringTrigger = nanoid()
          this._updateTrialEndsAtStringIdInterval = setInterval(() => {
            runInAction(() => {
              this._updateTrialEndsAtStringTrigger = nanoid()
            })
          }, 1000)
        }

        new ChannelsOrganization(this._entities.websocket).subscribeChannels(this)
        this._entities.segmentsStore.addItems(organization.organization_filters)
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async () => {
    await this.fetchOrganization()
    this._setFinishInit?.()
  }

  updateBalance = (data: IResponseEventUpdateBalance) => {
    this._accountCredits = data.account_credits
    this._trialCredits = data.trial_credits
    this._account_balance = data.account_balance
  }

  updateOrganizationCall = (data: IResponseEventOrganizationCalling) => {
    this._organization_call = data.organization_call
  }

  updateOrganizationCallAutoRecord = (isActive: boolean) => {
    if (this._organization_call?.call_auto_record) {
      this._organization_call = {
        ...this._organization_call,
        call_auto_record: {
          ...this._organization_call.call_auto_record,
          is_active: isActive,
        },
      }
    }
  }

  updateOrganizationCallViaAircall = (isActive: boolean) => {
    if (this._organization_call?.call_via_aircall) {
      this._organization_call = {
        ...this._organization_call,
        call_via_aircall: {
          ...this._organization_call.call_via_aircall,
          is_active: isActive,
        },
      }
    }
  }

  updatePhoneCheckerEnable = async (status: boolean) => {
    try {
      const { data } = await OrganizationApi.updatePhoneCheckerEnable({
        check_existing_contacts: status,
      })

      this._phone_checker = data
    } catch (e) {
      console.log(e)
    }
  }

  setPhoneChecker = (phoneChecker: IResponseOrganization['phone_checker'] | null) => {
    this._phone_checker = phoneChecker
  }

  updateFallbackTeamId = async (id: number) => {
    runInAction(() => {
      this._fallback_team_id = id
    })
    const { data } = await OrganizationApi.updateFallbackTeamId(id)
    if (data?.fallback_team_id !== this._fallback_team_id) {
      runInAction(() => {
        this._fallback_team_id = data?.fallback_team_id || null
      })
    }
  }

  updateOrganizationCallAutoCreateConversations = (isActive: boolean) => {
    this._create_new_conv_via_aircall = isActive
  }

  updateOrganizationNumbersGracePeriod = (status: boolean) => {
    this._is_numbers_grace_period = status
  }

  get doubleOptInEnabled() {
    return this._double_opt_in.is_active
  }

  get isOutOfAccountCredits() {
    return this._accountCredits <= 0
  }

  get isOutOfCredits() {
    const { isTrial } = this._entities.subscriptionStore
    const credits = isTrial ? this._trialCredits : this._accountCredits

    return credits <= 0
  }

  get trialEndsAt() {
    return this._trial_ends_at
  }

  get trialEndsAtDaysNumber() {
    return Math.round(Math.abs(uiStore.dayjs().diff(this._trial_ends_at, 'hour') / 24))
  }

  get formattedAccountCredits() {
    return numberFormat({ value: this._accountCredits })
  }

  get formattedTrialCredits() {
    return numberFormat({ value: this._trialCredits })
  }

  get isOutOfTrialCredits() {
    return this._trialCredits <= 0
  }

  get isAgency() {
    return this._is_agency
  }

  get accountBalance() {
    if (!this._account_balance) return 0

    return this._account_balance
  }

  get isBillableNumberVendor() {
    return Boolean(this._number_vendor?.isBillable)
  }

  get isOrganizationOwner() {
    return this._entities.usersStore.user?.id === this._owner_id
  }

  get phoneChecker() {
    return this._phone_checker
  }

  get isPhoneCheckerEnabled() {
    return Boolean(this._phone_checker?.is_active)
  }

  get isEnabledOrganisationCallAutoRecord() {
    return this._organization_call?.call_auto_record?.is_active
  }

  get numberVendor() {
    return this._number_vendor
  }

  get fallbackTeamId() {
    return this._fallback_team_id
  }

  get hashId() {
    return this._hash_id
  }

  get autoCloseConversation() {
    return this._auto_close_setting
  }

  get autoCloseDays() {
    return this._auto_close_days
  }

  get autoCloseUnsubscribe() {
    return this._auto_close_unsubscribe
  }

  get globalConversationHistory() {
    return this._use_global_conversation_history
  }

  get autoAssignContactOwner() {
    return this._auto_assign_contact_owner
  }

  get createNewConvViaAircall() {
    return this._create_new_conv_via_aircall
  }

  get extendedInternationalTexting() {
    return this._extended_international_texting
  }

  get isTwoFaEnabled() {
    return Boolean(this._two_fa_enabled)
  }

  get isNumbersGracePeriod() {
    return this._is_numbers_grace_period
  }

  get voicemailGreetingsAttachments() {
    return this._voicemailGreetingsAttachments
  }

  get isHipaaCompliant() {
    return this._is_hipaa_compliant
  }

  get hipaaEnabledAt() {
    return this._hipaa_enabled_at
  }

  get franchise() {
    return this._franchise
  }

  get clientsPlan() {
    return this._clients_plan
  }

  addVoicemailGreetingsAttachments = (item: Attachment) => {
    this._voicemailGreetingsAttachments = [...this._voicemailGreetingsAttachments, item]
  }

  setVoicemailGreetingsAttachments = (items: Attachment[]) => {
    this._voicemailGreetingsAttachments = items
  }

  get organizationExistingDays() {
    if (!this._created_at) return 0

    const createdDate = new Date(this._created_at).getTime()
    const today = new Date().getTime()

    const diffInMs = today - createdDate

    return Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  }

  handleTrackOrganizationActivity = async (data: IParamsTrackOrganizationActivity) => {
    try {
      await OrganizationApi.trackOrganizationActivity(data)
    } catch (error) {
      logger.error(error)
    }
  }

  get quietHoursSettings() {
    return this._quietHoursSettings
  }

  updateOrganizationQuietHoursSettings = (params: IParamsOrganizationQuietHours) => {
    this._quietHoursSettings = params
  }
}
